import { ReactElement } from 'react'
import { Stack, Typography } from '@mui/material'
import { countFailedOpenings } from '@editor/components/IFCElementForm/components/ifcParseReport'
import { useMutation } from '@tanstack/react-query'
import { MagicButton } from '@ui/actions'
import { useSnackbar } from '@ui/snackbar'
import { useControlStore, useModelStore, useSceneStore } from '@editorStores'
import { useModelInvalidation } from '@structuralPlanningHooks'
import { IFCOpeningParseReport, startIfcOpeningParse } from '@mutations'
import { getAlignmentMatrix, getGuidToAlignedBox } from '../../utils/glbElementGeometry'
import { matchOpeningsToWalls } from './utils'

interface Props {
  /** Selected IFC opening element guids. */
  guids: string[]
  projectId: string
  activeGlbFile: FileItem | null
  onSuccess: () => void
}

interface AutoImportResult {
  importedCount: number
  selectedCount: number
  /** Openings sent to the parse task that it could not import. */
  failedCount: number
}

/**
 * "Automatisch" tab of the opening import step: infers the target wall of every selected IFC
 * opening from its GLB geometry (see `matchOpeningsToWalls`) and imports all matched openings with
 * a single parse request. Openings without a matching wall are skipped; a failing request is
 * deliberately silent (the user can fall back to the "Erweitert" tab).
 */
const AutoOpeningImportTab = ({
  guids,
  projectId,
  activeGlbFile,
  onSuccess,
}: Props): ReactElement => {
  const { enqueueSnackbar } = useSnackbar()
  const invalidateModel = useModelInvalidation()
  const walls = useModelStore(state => state.model.walls)
  const applyIfcAlignmentConfig = useControlStore(state => state.applyIfcAlignmentConfig)
  const loadedGltfModels = useSceneStore(state => state.loadedGltfModels)

  const gltfModel = activeGlbFile ? loadedGltfModels[activeGlbFile.guid] : null
  // Frontend shows the GLB (converted from IFC); the parse task needs the raw IFC file id = parent.
  const ifcFileId = activeGlbFile?.parent_guid ?? null

  const { mutate: autoImport, isLoading } = useMutation({
    mutationFn: async (): Promise<AutoImportResult> => {
      if (!projectId || !ifcFileId || !gltfModel || guids.length === 0)
        return { importedCount: 0, selectedCount: guids.length, failedCount: 0 }

      const alignmentMatrix = getAlignmentMatrix(
        activeGlbFile?.additional_data?.alignment_config,
        applyIfcAlignmentConfig,
      )
      const openingBoxes = getGuidToAlignedBox(gltfModel, new Set(guids), alignmentMatrix)
      const openingToWall = matchOpeningsToWalls(openingBoxes, walls)

      // The parse task skips openings already imported into their wall, so don't count them
      const configs = Object.entries(openingToWall)
        .filter(([ifcElementId, wallGuid]) => {
          const wall = walls.find(w => w.guid === wallGuid)
          return !(wall?.openings || []).some(
            opening => opening.ifcElementId === ifcElementId || opening.guid === ifcElementId,
          )
        })
        .map(([ifcElementId, wallGuid]) => ({
          ifc_element_id: ifcElementId,
          ifc_file_id: ifcFileId,
          parsedWallId: wallGuid,
          limitOpeningXYCoords: true,
        }))
      if (configs.length === 0)
        return { importedCount: 0, selectedCount: guids.length, failedCount: 0 }

      const report = (await startIfcOpeningParse.request(projectId, configs)) as
        | IFCOpeningParseReport
        | undefined
      const failedCount = countFailedOpenings(report)
      return {
        importedCount: configs.length - failedCount,
        selectedCount: guids.length,
        failedCount,
      }
    },
    onSuccess: async ({ importedCount, selectedCount, failedCount }: AutoImportResult) => {
      if (importedCount === 0 && failedCount === 0) {
        enqueueSnackbar('Keine passende Wand zu den gewählten Öffnungen gefunden', {
          variant: 'info',
        })
        return
      }
      if (projectId) await invalidateModel(projectId)
      enqueueSnackbar(
        importedCount < selectedCount
          ? `${importedCount} von ${selectedCount} Öffnungen automatisch importiert`
          : `${importedCount} ${
              importedCount === 1 ? 'Öffnung' : 'Öffnungen'
            } automatisch importiert`,
        // Some matched openings could not be imported (see the parse report).
        { variant: failedCount > 0 ? 'warning' : 'success' },
      )
      onSuccess()
    },
    // The automatic flow deliberately fails silently (no error snackbar); just refresh the model
    // so any openings imported before the failure show up.
    onError: async () => {
      if (projectId) await invalidateModel(projectId)
    },
  })

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Wählen Sie im Modell alle Öffnungen aus, die im Modell noch fehlen. Die zugehörige Wand wird
        für jede Öffnung automatisch anhand der Geometrie ermittelt und die Öffnungen werden
        gemeinsam importiert.
      </Typography>

      <MagicButton
        variant="contained"
        size="small"
        fullWidth
        onClick={() => autoImport()}
        disabled={!projectId || !ifcFileId || !gltfModel || guids.length === 0}
        loading={isLoading}
        data-cy="btn-ifc-auto-opening-import"
      >
        Automatisch importieren
      </MagicButton>
    </Stack>
  )
}

export default AutoOpeningImportTab
